import React, { lazy, Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import { useEnsureDefaultWorkspace } from "@/hooks/useEnsureDefaultWorkspace";
import {
  FloatingAgentCompanion,
  isActiveCompanionWorkflow,
  useAgentChatStore,
} from "@/features/companion";
import { apiFetch } from "@/lib/jwt-auth-client";
import { AppSidebar } from "./sidebar/AppSidebar";
import { MobileBottomNav } from "./sidebar/MobileBottomNav";
import { BreadcrumbProvider } from "@/contexts/BreadcrumbContext";
import { AppHeaderProvider } from "@/contexts/AppHeaderContext";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { MarcusOnboardingController } from "@/features/ads/components/marcus/MarcusOnboardingOverlay";

const NovaCompanionPanel = lazy(() =>
  import("@/features/content/components/NovaCompanionPanel").then((m) => ({
    default: m.NovaCompanionPanel,
  })),
);

const SagaCompanionPanel = lazy(() =>
  import("@/components/social-studio/saga/SagaCompanionPanel").then((m) => ({
    default: m.SagaCompanionPanel,
  })),
);

/** Content Studio routes — Nova panel should persist across these. */
const CONTENT_STUDIO_RE = /\/workspaces\/[^/]+\/departments\/marketing\/content/;
/** Social Studio routes — Saga panel should persist across these. Includes
 *  the canonical short URL `/organic-campaigns` (Zeely-nav rewrite) and the
 *  legacy workspace-scoped department route. */
const SOCIAL_STUDIO_RE = /\/workspaces\/[^/]+\/departments\/marketing\/social/;
const SOCIAL_STUDIO_SHORT = "/organic-campaigns";

/**
 * Routes where the page-level `<FloatingAgentCompanion>` must NOT render —
 * either because the page mounts its own docked panel (Nova / Saga), because
 * Marcus is invoked through a workspace-bound flow (Ads Studio), or because
 * the page deliberately suppresses the global companion.
 *
 * Phase 14 of the Zeely-Mirror plan will remove `/video-ads` from this list
 * once the new docked Stella panel is wired up. Until then, the page keeps
 * its current "no companion" UX.
 */
const AGENT_SUPPRESSED_ROUTE_ROOTS: readonly string[] = [
  "/ads",
  "/static-ads",
  "/video-ads",
  "/projects",
  "/asset-library",
  "/paid-campaigns",
  SOCIAL_STUDIO_SHORT,
];

const AGENT_SUPPRESSED_PREFIXES: readonly string[] = ["/departments", "/coagent"];

export function isAgentCompanionSuppressed(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  for (const prefix of AGENT_SUPPRESSED_PREFIXES) {
    if (pathname.startsWith(prefix)) return true;
  }
  if (/\/workspaces\/[^/]+\/departments/.test(pathname)) return true;
  for (const routeRoot of AGENT_SUPPRESSED_ROUTE_ROOTS) {
    if (pathname === routeRoot || pathname.startsWith(`${routeRoot}/`)) return true;
  }
  return false;
}

type CreditsPayload = {
  plan?: string;
  status?: string;
  daysLeft?: number | null;
};

export function AppShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: session } = useAuth();
  const [subscription, setSubscription] = useState<CreditsPayload | null>(null);
  const connectedWorkflowSlug = useAgentChatStore((s) => s.connectedWorkflowSlug);
  const connectedSessionId = useAgentChatStore((s) => s.connectedSessionId);
  const {
    fetchOrganizations,
    organizations,
    loaded: organizationsLoaded,
    activeOrganizationId,
  } = useOrganizationStore();

  // Aigentise — once the user is authenticated and has an active
  // organization, make sure a default workspace exists. Backend
  // returns an existing one or auto-creates "Default". Hook is
  // a no-op when a default is already stored, so the cost is one
  // localStorage read per app-shell mount on the common path.
  useEnsureDefaultWorkspace({
    activeOrganizationId,
    enabled: Boolean(session?.user) && organizationsLoaded && organizations.length > 0,
  });

  // Redirect to onboarding if no organizations or if pending invites exist
  useEffect(() => {
    if (!session?.user) return;

    async function checkOnboarding() {
      let orgs = organizations;
      if (!organizationsLoaded) {
        orgs = await fetchOrganizations();
      }
      if (orgs.length === 0) {
        navigate("/onboarding", { replace: true });
        return;
      }
      // Check for pending invites — redirect to onboarding to show them
      if (!pathname?.startsWith("/onboarding")) {
        try {
          const res = await apiFetch("/auth/invites/");
          if (res.ok) {
            const invites = await res.json();
            if (Array.isArray(invites) && invites.length > 0) {
              navigate("/onboarding", { replace: true });
            }
          }
        } catch {}
      }
    }
    void checkOnboarding();
  }, [
    session?.user,
    organizationsLoaded,
    organizations.length,
    fetchOrganizations,
    navigate,
    pathname,
  ]);

  useEffect(() => {
    let isMounted = true;

    async function fetchCreditsState() {
      if (!session?.user) {
        setSubscription(null);
        return;
      }

      try {
        const response = await apiFetch("/credits/", { cache: "no-store" });
        if (!response.ok) return;
        const payload = (await response.json()) as CreditsPayload;
        if (isMounted) {
          setSubscription(payload);
        }
      } catch {
        if (isMounted) {
          setSubscription(null);
        }
      }
    }

    void fetchCreditsState();

    return () => {
      isMounted = false;
    };
  }, [session?.user?.id]);

  return (
    <div className="flex h-app-viewport w-full overflow-hidden bg-background text-foreground">
      <AppSidebar />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <BreadcrumbProvider>
          <AppHeaderProvider>
            <main className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background">
              <Breadcrumb />
              <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">{children}</div>
            </main>
          </AppHeaderProvider>
        </BreadcrumbProvider>
        <MobileBottomNav />
      </div>

      {CONTENT_STUDIO_RE.test(pathname ?? "") && (
        <Suspense fallback={null}>
          <NovaCompanionPanel />
        </Suspense>
      )}

      {(SOCIAL_STUDIO_RE.test(pathname ?? "") || pathname === SOCIAL_STUDIO_SHORT) && (
        <Suspense fallback={null}>
          <SagaCompanionPanel />
        </Suspense>
      )}

      {!isAgentCompanionSuppressed(pathname) &&
        isActiveCompanionWorkflow(connectedWorkflowSlug) && <FloatingAgentCompanion />}
      <MarcusOnboardingController
        userId={session?.user?.id ?? null}
        pathname={pathname}
        connectedWorkflowSlug={connectedWorkflowSlug}
        connectedSessionId={connectedSessionId}
      />
    </div>
  );
}
